import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ExternalLink, Plus, Pencil, Trash2, Search, Archive, ArchiveRestore, Globe, Building2,
  Link2, Copy, BarChart3, Tag,
} from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

import {
  listAffiliateCenters, upsertAffiliateCenter, deleteAffiliateCenter, recordCenterOpen,
} from "@/lib/affiliate-centers.functions";
import {
  listAffiliateLinks, upsertAffiliateLink, deleteAffiliateLink, listLinkClicks,
} from "@/lib/promotional-links.functions";
import { listMyAccounts, listMyWorkspaces } from "@/lib/workspaces.functions";

export const Route = createFileRoute("/_authenticated/links/")({
  component: LinksHubPage,
});

function faviconFor(url: string): string | null {
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=128`;
  } catch {
    return null;
  }
}

function LinksHubPage() {
  return (
    <div className="container mx-auto max-w-7xl p-6 space-y-10">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Link Vault</h1>
        <p className="text-sm text-muted-foreground">
          Affiliate centers, promotional links, and click attribution.
        </p>
      </div>
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold tracking-tight">Affiliate Centers</h2>
        </div>
        <CentersPanel />
      </section>
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold tracking-tight">Promotional Links</h2>
        </div>
        <LinksPanel />
      </section>
    </div>
  );
}

/* =========================================================================
   CENTERS PANEL
   ========================================================================= */
function CentersPanel() {
  const qc = useQueryClient();
  const fetchAccts = useServerFn(listMyAccounts);
  const fetchWs = useServerFn(listMyWorkspaces);
  const fetchCenters = useServerFn(listAffiliateCenters);
  const upsertFn = useServerFn(upsertAffiliateCenter);
  const deleteFn = useServerFn(deleteAffiliateCenter);
  const openFn = useServerFn(recordCenterOpen);

  const { data: acctData } = useQuery({ queryKey: ["my-accounts"], queryFn: () => fetchAccts() });
  const { data: wsData } = useQuery({ queryKey: ["my-workspaces"], queryFn: () => fetchWs() });
  const account = acctData?.accounts?.[0];
  const workspaces = wsData?.workspaces ?? [];

  const [search, setSearch] = useState("");
  const [workspaceFilter, setWorkspaceFilter] = useState<string>("all");
  const [showArchived, setShowArchived] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const { data: centersData, isLoading } = useQuery({
    enabled: !!account,
    queryKey: ["affiliate-centers", account?.id, showArchived],
    queryFn: () => fetchCenters({ data: { account_id: account!.id, include_archived: showArchived } }),
  });

  const centers = useMemo(() => {
    const all = centersData?.centers ?? [];
    return all.filter((c: any) => {
      if (workspaceFilter !== "all" && c.workspace_id !== workspaceFilter) return false;
      if (search.trim()) {
        const s = search.trim().toLowerCase();
        return c.display_name?.toLowerCase().includes(s) ||
          c.login_url?.toLowerCase().includes(s) ||
          c.notes?.toLowerCase().includes(s);
      }
      return true;
    });
  }, [centersData, workspaceFilter, search]);

  const upsertMut = useMutation({
    mutationFn: (payload: any) => upsertFn({ data: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["affiliate-centers"] });
      setEditorOpen(false);
      setEditing(null);
      toast.success("Saved");
    },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["affiliate-centers"] });
      toast.success("Deleted");
    },
    onError: (e: any) => toast.error(e?.message ?? "Delete failed"),
  });

  const handleOpen = (c: any) => {
    void openFn({ data: { center_id: c.id } }).catch(() => {});
    const url = c.login_url.startsWith("http") ? c.login_url : `https://${c.login_url}`;
    if (c.open_in_new_tab) window.open(url, "_blank", "noopener,noreferrer");
    else window.location.href = url;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search centers…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
        </div>
        <Select value={workspaceFilter} onValueChange={setWorkspaceFilter}>
          <SelectTrigger className="w-[220px]"><SelectValue placeholder="All workspaces" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All workspaces</SelectItem>
            {workspaces.map((w: any) => (<SelectItem key={w.id} value={w.id}>{w.program_name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Tabs value={showArchived ? "archived" : "active"} onValueChange={(v) => setShowArchived(v === "archived")}>
          <TabsList>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="archived">Archived</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button onClick={() => { setEditing(null); setEditorOpen(true); }} disabled={!account || workspaces.length === 0}>
          <Plus className="mr-2 h-4 w-4" /> Add center
        </Button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p>
        : centers.length === 0 ? (
          <EmptyState
            icon={Building2}
            title={workspaces.length === 0 ? "Create a workspace first" : "Add your first affiliate center"}
            description={workspaces.length === 0
              ? "Affiliate centers live inside workspaces. Spin one up to start tracking logins."
              : "Save the login URL for each program so you can jump in with one click."}
            action={workspaces.length > 0 ? (
              <Button onClick={() => { setEditing(null); setEditorOpen(true); }}>
                <Plus className="mr-2 h-4 w-4" /> Add center
              </Button>
            ) : undefined}
          />
        ) : (
          <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
            {centers.map((c: any) => {
              const ws = workspaces.find((w: any) => w.id === c.workspace_id);
              const logo = c.logo_url || faviconFor(c.login_url);
              return (
                <Card key={c.id} className="group hover:border-primary/50 transition-colors">
                  <CardContent className="p-4 space-y-3">
                    <button onClick={() => handleOpen(c)} className="w-full text-left space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center overflow-hidden shrink-0">
                          {logo ? <img src={logo} alt="" className="h-8 w-8 object-contain" /> : <Globe className="h-5 w-5 text-muted-foreground" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{c.display_name}</div>
                          {ws && <div className="text-xs text-muted-foreground truncate">{ws.program_name}</div>}
                        </div>
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      {c.notes && <p className="text-xs text-muted-foreground line-clamp-2">{c.notes}</p>}
                    </button>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{c.open_count ?? 0} opens</span>
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(c); setEditorOpen(true); }}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => upsertMut.mutate({
                            id: c.id, account_id: c.account_id, workspace_id: c.workspace_id,
                            display_name: c.display_name, login_url: c.login_url,
                            logo_url: c.logo_url, notes: c.notes, open_in_new_tab: c.open_in_new_tab,
                            is_archived: !c.is_archived,
                          })}
                          title={c.is_archived ? "Restore" : "Archive"}>
                          {c.is_archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-destructive"
                          onClick={() => { if (confirm(`Delete "${c.display_name}"?`)) deleteMut.mutate(c.id); }}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    {c.is_archived && <Badge variant="secondary" className="text-[10px]">Archived</Badge>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      <CenterEditor open={editorOpen} onOpenChange={(v: boolean) => { setEditorOpen(v); if (!v) setEditing(null); }}
        existing={editing} accountId={account?.id} workspaces={workspaces}
        onSave={(payload: any) => upsertMut.mutate(payload)} saving={upsertMut.isPending} />
    </div>
  );
}

function CenterEditor({ open, onOpenChange, existing, accountId, workspaces, onSave, saving }: any) {
  const [displayName, setDisplayName] = useState("");
  const [loginUrl, setLoginUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const [openInNewTab, setOpenInNewTab] = useState(true);

  useMemo(() => {
    if (open) {
      setDisplayName(existing?.display_name ?? "");
      setLoginUrl(existing?.login_url ?? "");
      setLogoUrl(existing?.logo_url ?? "");
      setNotes(existing?.notes ?? "");
      setWorkspaceId(existing?.workspace_id ?? workspaces[0]?.id ?? "");
      setOpenInNewTab(existing?.open_in_new_tab ?? true);
    }
  }, [open, existing, workspaces]);

  const handleUrlBlur = () => {
    if (loginUrl && !logoUrl) {
      const f = faviconFor(loginUrl);
      if (f) setLogoUrl(f);
    }
  };

  const handleSave = () => {
    if (!accountId || !workspaceId || !displayName.trim() || !loginUrl.trim()) {
      toast.error("Name, workspace, and login URL are required"); return;
    }
    let url = loginUrl.trim();
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    onSave({
      id: existing?.id, account_id: accountId, workspace_id: workspaceId,
      display_name: displayName.trim(), login_url: url,
      logo_url: logoUrl.trim() || null, notes: notes.trim() || null,
      open_in_new_tab: openInNewTab, is_archived: existing?.is_archived ?? false,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit center" : "Add affiliate center"}</DialogTitle>
          <DialogDescription>Quick-launch tile for a program's affiliate dashboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Workspace</Label>
            <Select value={workspaceId} onValueChange={setWorkspaceId}>
              <SelectTrigger><SelectValue placeholder="Select workspace" /></SelectTrigger>
              <SelectContent>{workspaces.map((w: any) => (<SelectItem key={w.id} value={w.id}>{w.program_name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div><Label>Display name</Label><Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></div>
          <div><Label>Login URL</Label><Input value={loginUrl} onChange={(e) => setLoginUrl(e.target.value)} onBlur={handleUrlBlur} placeholder="https://affiliates.example.com" /></div>
          <div><Label>Logo URL (optional)</Label>
            <div className="flex items-center gap-2">
              {logoUrl && <img src={logoUrl} alt="" className="h-8 w-8 rounded border object-contain" />}
              <Input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="Auto-filled from URL" />
            </div>
          </div>
          <div><Label>Notes (optional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} /></div>
          <div className="flex items-center justify-between"><Label className="cursor-pointer">Open in new tab</Label><Switch checked={openInNewTab} onCheckedChange={setOpenInNewTab} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================================
   PROMOTIONAL LINKS PANEL
   ========================================================================= */
function LinksPanel() {
  const qc = useQueryClient();
  const fetchAccts = useServerFn(listMyAccounts);
  const fetchWs = useServerFn(listMyWorkspaces);
  const fetchLinks = useServerFn(listAffiliateLinks);
  const upsertFn = useServerFn(upsertAffiliateLink);
  const deleteFn = useServerFn(deleteAffiliateLink);

  const { data: acctData } = useQuery({ queryKey: ["my-accounts"], queryFn: () => fetchAccts() });
  const { data: wsData } = useQuery({ queryKey: ["my-workspaces"], queryFn: () => fetchWs() });
  const account = acctData?.accounts?.[0];
  const workspaces = wsData?.workspaces ?? [];

  const [search, setSearch] = useState("");
  const [workspaceFilter, setWorkspaceFilter] = useState<string>("all");
  const [showArchived, setShowArchived] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [statsLinkId, setStatsLinkId] = useState<string | null>(null);

  const { data: linksData, isLoading } = useQuery({
    enabled: !!account,
    queryKey: ["affiliate-links", account?.id, showArchived, workspaceFilter, search],
    queryFn: () => fetchLinks({
      data: {
        account_id: account!.id,
        workspace_id: workspaceFilter !== "all" ? workspaceFilter : undefined,
        include_archived: showArchived,
        search: search || undefined,
      },
    }),
  });
  const links = linksData?.links ?? [];

  const upsertMut = useMutation({
    mutationFn: (payload: any) => upsertFn({ data: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["affiliate-links"] });
      setEditorOpen(false); setEditing(null);
      toast.success("Saved");
    },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["affiliate-links"] });
      toast.success("Deleted");
    },
    onError: (e: any) => toast.error(e?.message ?? "Delete failed"),
  });


  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed")
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search links…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
        </div>
        <Select value={workspaceFilter} onValueChange={setWorkspaceFilter}>
          <SelectTrigger className="w-[220px]"><SelectValue placeholder="All workspaces" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All workspaces</SelectItem>
            {workspaces.map((w: any) => (<SelectItem key={w.id} value={w.id}>{w.program_name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Tabs value={showArchived ? "archived" : "active"} onValueChange={(v) => setShowArchived(v === "archived")}>
          <TabsList>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="archived">Archived</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button onClick={() => { setEditing(null); setEditorOpen(true); }} disabled={!account || workspaces.length === 0}>
          <Plus className="mr-2 h-4 w-4" /> New link
        </Button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p>
        : links.length === 0 ? (
          <EmptyState
            icon={Link2}
            title="Add your first promotional link"
            description="Create trackable URLs for every campaign, clicks, EPC, and revenue roll up here automatically."
            action={(
              <Button onClick={() => { setEditing(null); setEditorOpen(true); }} disabled={!account || workspaces.length === 0}>
                <Plus className="mr-2 h-4 w-4" /> New link
              </Button>
            )}
          />
        ) : (
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Link</TableHead>
                  <TableHead>Workspace</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                  <TableHead className="text-right">Unique</TableHead>
                  <TableHead className="text-right">EPC</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="w-[160px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.map((l: any) => {
                  const ws = workspaces.find((w: any) => w.id === l.workspace_id);
                  return (
                    <TableRow key={l.id}>
                      <TableCell>
                        <div className="font-medium">{l.label}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[320px]">{l.destination_url}</div>
                        {l.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {l.tags.map((t: string) => (
                              <Badge key={t} variant="secondary" className="text-[10px]"><Tag className="h-2.5 w-2.5 mr-1" />{t}</Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{ws?.program_name ?? ","}</TableCell>
                      <TableCell className="text-right tabular-nums">{l.click_count ?? 0}</TableCell>
                      <TableCell className="text-right tabular-nums">{l.unique_click_count ?? 0}</TableCell>
                      <TableCell className="text-right tabular-nums">${Number(l.epc ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right tabular-nums">${Number(l.attributed_revenue ?? 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 justify-end">
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Copy destination URL" onClick={() => copy(l.destination_url)}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Recent clicks" onClick={() => setStatsLinkId(l.id)}>
                            <BarChart3 className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(l); setEditorOpen(true); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7"
                            onClick={() => upsertMut.mutate({
                              id: l.id, account_id: l.account_id, workspace_id: l.workspace_id,
                              label: l.label, destination_url: l.destination_url,
                              tags: l.tags, is_archived: !l.is_archived,
                            })}
                            title={l.is_archived ? "Restore" : "Archive"}>
                            {l.is_archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-destructive"
                            onClick={() => { if (confirm(`Delete "${l.label}"?`)) deleteMut.mutate(l.id); }}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent></Card>
        )}

      <LinkEditor open={editorOpen} onOpenChange={(v: boolean) => { setEditorOpen(v); if (!v) setEditing(null); }}
        existing={editing} accountId={account?.id} workspaces={workspaces}
        onSave={(payload: any) => upsertMut.mutate(payload)} saving={upsertMut.isPending} />

      <ClickStatsDialog linkId={statsLinkId} onClose={() => setStatsLinkId(null)} />
    </div>
  );
}

function LinkEditor({ open, onOpenChange, existing, accountId, workspaces, onSave, saving }: any) {
  const [label, setLabel] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");

  useMemo(() => {
    if (open) {
      setLabel(existing?.label ?? "");
      setDestinationUrl(existing?.destination_url ?? "");
      setProductName(existing?.product_name ?? "");
      setDescription(existing?.description ?? "");
      setWorkspaceId(existing?.workspace_id ?? workspaces[0]?.id ?? "");
      setTagsText((existing?.tags ?? []).join(", "));
      setUtmSource(existing?.utm_source ?? "");
      setUtmMedium(existing?.utm_medium ?? "");
      setUtmCampaign(existing?.utm_campaign ?? "");
    }
  }, [open, existing, workspaces]);

  const handleSave = () => {
    if (!accountId || !workspaceId || !label.trim() || !destinationUrl.trim()) {
      toast.error("Label, workspace, and destination URL are required"); return;
    }
    let url = destinationUrl.trim();
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    const tags = tagsText.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 20);
    onSave({
      id: existing?.id, account_id: accountId, workspace_id: workspaceId,
      label: label.trim(), destination_url: url,
      product_name: productName.trim() || null,
      description: description.trim() || null,
      tags,
      utm_source: utmSource.trim() || null,
      utm_medium: utmMedium.trim() || null,
      utm_campaign: utmCampaign.trim() || null,
      is_archived: existing?.is_archived ?? false,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit link" : "New promotional link"}</DialogTitle>
          <DialogDescription>Trackable short link with UTM tagging.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Workspace</Label>
            <Select value={workspaceId} onValueChange={setWorkspaceId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{workspaces.map((w: any) => (<SelectItem key={w.id} value={w.id}>{w.program_name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div><Label>Label</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ClickFunnels Black Friday promo" /></div>
          <div><Label>Destination URL</Label><Input value={destinationUrl} onChange={(e) => setDestinationUrl(e.target.value)} placeholder="https://clickfunnels.com/?aff=123" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Product (optional)</Label><Input value={productName} onChange={(e) => setProductName(e.target.value)} /></div>
            <div><Label>Tags (comma sep.)</Label><Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="bf2025, email" /></div>
          </div>
          <div><Label>Description</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">utm_source</Label><Input value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="email" /></div>
            <div><Label className="text-xs">utm_medium</Label><Input value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="newsletter" /></div>
            <div><Label className="text-xs">utm_campaign</Label><Input value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="bf2025" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClickStatsDialog({ linkId, onClose }: { linkId: string | null; onClose: () => void }) {
  const fetchClicks = useServerFn(listLinkClicks);
  const { data, isLoading } = useQuery({
    enabled: !!linkId,
    queryKey: ["link-clicks", linkId],
    queryFn: () => fetchClicks({ data: { link_id: linkId!, limit: 100 } }),
  });
  return (
    <Dialog open={!!linkId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Recent clicks</DialogTitle>
          <DialogDescription>Last 100 clicks for this link.</DialogDescription>
        </DialogHeader>
        {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p>
          : (data?.clicks ?? []).length === 0 ? <p className="text-sm text-muted-foreground">No clicks yet.</p>
          : (
            <div className="max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>When</TableHead><TableHead>Location</TableHead>
                  <TableHead>Referrer</TableHead><TableHead className="text-right">Converted</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(data?.clicks ?? []).map((c: any) => (
                    <TableRow key={c.id}>
                      <TableCell className="text-xs">{new Date(c.clicked_at).toLocaleString()}</TableCell>
                      <TableCell className="text-xs">{[c.city, c.region, c.country].filter(Boolean).join(", ") || ","}</TableCell>
                      <TableCell className="text-xs truncate max-w-[240px]">{c.referer ?? ","}</TableCell>
                      <TableCell className="text-right text-xs">{c.converted ? `$${Number(c.conversion_value ?? 0).toFixed(2)}` : ","}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}
